import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Sheet, SheetContent, SheetBody, SheetFooter } from '../components/ui/Sheet';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { TOTP_LOCKED_MESSAGE, isTotpLocked } from '../lib/authErrors';
import {
  STEP_UP_ENDED_MESSAGE,
  isStepUpRequired,
  stepUpBody,
  stepUpCodeReady,
  useStepUpWindow,
} from '../lib/stepUp';
import { StepUpCodeField } from '../components/StepUpCodeField';
import {
  CustomRolesSection,
  MemberRoleSelect,
  useCustomRoles,
} from '../components/CustomRolesSection';
import {
  ApiError,
  type CreateInviteResponse,
  type Invitation,
  type MemberResetAccessResponse,
  type Role,
  type RevokeAllSessionsResponse,
  type TenantUser,
} from '../types/api';

const REVOKE_ALL_CONFIRM_WORD = 'REVOKE';

function stepUpErrorText(err: unknown): string {
  if (isTotpLocked(err)) return TOTP_LOCKED_MESSAGE;
  if (isStepUpRequired(err)) return STEP_UP_ENDED_MESSAGE;
  if (err instanceof ApiError && err.status === 401)
    return 'The code is incorrect or already used.';
  if (err instanceof ApiError && err.status === 400 && err.body.error === 'cannot_reset_self') {
    return 'You cannot reset your own account from here.';
  }
  if (err instanceof ApiError && err.status === 404)
    return 'The user does not exist in this account.';
  // Reset issued and the user's sessions revoked, but the mail with the
  // link itself could not be sent (re-review 2026-09-29) — a new attempt
  // issues a new token, which invalidates the old one.
  if (err instanceof ApiError && err.status === 502 && err.body.error === 'mail_failed') {
    return 'The mail could not be sent – try again.';
  }
  if (err instanceof ApiError && err.status === 409 && err.body.message) return err.body.message;
  if (err instanceof ApiError && err.body.message) return err.body.message;
  return 'The action could not be completed. Try again.';
}

/**
 * Right-hand step-up sheet for "Reset access" (runbook MANGLER item 4,
 * docs/decisions.md "Admin-udløst nulstilling af adgang" — redesigned
 * 2026-09-29 after a security review found the original two-action design
 * let an admin take over a member's account). Same fresh-TOTP pattern as
 * `WooPage.tsx`'s `SetKeySheet`. The admin never sees a token or link —
 * only that a mail was sent to the member's own address.
 */
function ResetMemberSheet({
  open,
  onOpenChange,
  target,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  target: TenantUser | null;
}) {
  const [totpCode, setTotpCode] = useState('');
  const queryClient = useQueryClient();
  const stepUp = useStepUpWindow();
  const codeId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<MemberResetAccessResponse>(`/users/${target?.id}/reset-access`, {
        method: 'POST',
        body: stepUp.active ? {} : stepUpBody(totpCode),
      }),
    onSuccess: () => {
      if (!stepUp.active) stepUp.onStepUpSuccess();
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: stepUp.onStepUpError,
  });

  const canSubmit =
    (stepUp.active || stepUpCodeReady(totpCode)) && !mutation.isPending && Boolean(target);

  function close() {
    onOpenChange(false);
    setTotpCode('');
    mutation.reset();
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(next);
      }}
    >
      {open && target ? (
        <SheetContent
          eyebrow="Security"
          title={`Reset access: ${target.email}`}
          description="Sends a one-time link to the user's own email. The user chooses a new password and must set up 2FA again. All of the user's sessions are revoked immediately."
        >
          {mutation.isSuccess ? (
            <SheetBody>
              <div role="status" className="text-success-text text-sm">
                Link sent to the user's email.
              </div>
            </SheetBody>
          ) : (
            <form
              id="reset-member-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) mutation.mutate();
              }}
            >
              <SheetBody>
                {mutation.isError ? <Alert>{stepUpErrorText(mutation.error)}</Alert> : null}
                <StepUpCodeField
                  id={codeId}
                  value={totpCode}
                  onChange={setTotpCode}
                  active={stepUp.active}
                  minutesLeft={stepUp.minutesLeft}
                  label="Your code from the authenticator app"
                />
              </SheetBody>
            </form>
          )}
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={close}>
              {mutation.isSuccess ? 'Close' : 'Cancel'}
            </Button>
            {mutation.isSuccess ? null : (
              <Button type="submit" form="reset-member-form" variant="danger" disabled={!canSubmit}>
                {mutation.isPending ? 'Sending…' : 'Confirm: reset access'}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

/**
 * Danger control for `POST /api/session/revoke-all` (runbook MANGLER item
 * 3): revokes every live session for the tenant, including the acting
 * admin's own — so `logout()` runs right after a successful call and the
 * shell's `ProtectedShell` sends everyone (this admin included) back to
 * `/login`.
 */
function RevokeAllSessionsSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [totpCode, setTotpCode] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const { logout } = useAuth();
  const stepUp = useStepUpWindow();
  const codeId = useId();
  const confirmId = useId();

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<RevokeAllSessionsResponse>('/session/revoke-all', {
        method: 'POST',
        body: stepUp.active ? {} : stepUpBody(totpCode),
      }),
    onError: stepUp.onStepUpError,
    onSuccess: () => {
      // The acting admin's own session is revoked too — log out locally
      // and let ProtectedShell redirect to /login (401 on the next call
      // is handled the same way logout already handles it elsewhere).
      void logout();
    },
  });

  const confirmMismatch =
    confirmText.length > 0 && confirmText.trim().toUpperCase() !== REVOKE_ALL_CONFIRM_WORD;
  const canSubmit =
    (stepUp.active || stepUpCodeReady(totpCode)) &&
    confirmText.trim().toUpperCase() === REVOKE_ALL_CONFIRM_WORD &&
    !mutation.isPending;

  function close() {
    onOpenChange(false);
    setTotpCode('');
    setConfirmText('');
    mutation.reset();
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(next);
      }}
    >
      {open ? (
        <SheetContent
          eyebrow="Security"
          title="Revoke all sessions"
          description="Logs out every user in this account immediately — yourself included. Use if you suspect a login has been compromised."
        >
          <form
            id="revoke-all-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) mutation.mutate();
            }}
          >
            <SheetBody>
              {mutation.isError ? <Alert>{stepUpErrorText(mutation.error)}</Alert> : null}
              <StepUpCodeField
                id={codeId}
                value={totpCode}
                onChange={setTotpCode}
                active={stepUp.active}
                minutesLeft={stepUp.minutesLeft}
                label="Your code from the authenticator app"
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={confirmId}>Type {REVOKE_ALL_CONFIRM_WORD} to confirm</Label>
                <Input
                  id={confirmId}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                  aria-describedby={`${confirmId}-hint`}
                  aria-invalid={confirmMismatch}
                />
                <div
                  id={`${confirmId}-hint`}
                  className="min-h-[18px] text-xs text-danger-text"
                  aria-live="polite"
                >
                  {confirmMismatch ? `Type exactly "${REVOKE_ALL_CONFIRM_WORD}"` : ''}
                </div>
              </div>
            </SheetBody>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" form="revoke-all-form" variant="danger" disabled={!canSubmit}>
              {mutation.isPending ? 'Revoking…' : 'Confirm: revoke all sessions'}
            </Button>
          </SheetFooter>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', editor: 'Editor', viewer: 'Viewer' };

function inviteErrorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 400) return 'Enter a valid email address.';
  if (err instanceof ApiError && err.status === 403) return 'Only administrators can invite users.';
  return 'The invite could not be created. Try again.';
}

function invitationStatus(i: Invitation): 'accepted' | 'expired' | 'pending' {
  if (i.acceptedAt !== null) return 'accepted';
  return i.expiresAt < Date.now() ? 'expired' : 'pending';
}

/**
 * Admin-only: create a one-time invite link for a colleague and list the
 * invitations. No mail is sent — the admin hands the link over. The token
 * only lives in this component's mutation state (shown once, never put in
 * storage or the query cache); it sits in the URL fragment so it never
 * reaches a server log or `Referer` header.
 */
function InviteSection() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const emailId = useId();
  const roleId = useId();
  const linkId = useId();

  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => apiFetch<{ invitations: Invitation[] }>('/invites'),
  });

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<CreateInviteResponse>('/invites', {
        method: 'POST',
        body: { email: email.trim(), role },
      }),
    onSuccess: () => {
      setEmail('');
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const created = mutation.data;
  const link = created ? `${window.location.origin}/invite#token=${created.inviteToken}` : '';
  const canSubmit = email.trim().length > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Card className="p-4 flex flex-col gap-3">
      <h2 className="text-sm font-semibold m-0">Invite user</h2>
      <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-1.5 min-w-[240px] flex-1">
          <Label htmlFor={emailId}>Email</Label>
          <Input
            id={emailId}
            type="email"
            autoComplete="off"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={roleId}>Role</Label>
          <select
            id={roleId}
            className="h-[42px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            <option value="admin">{ROLE_LABEL.admin}</option>
            <option value="editor">{ROLE_LABEL.editor}</option>
            <option value="viewer">{ROLE_LABEL.viewer}</option>
          </select>
        </div>
        <Button type="submit" disabled={!canSubmit}>
          {mutation.isPending ? 'Creating…' : 'Create invite link'}
        </Button>
      </form>

      {mutation.isError ? <Alert>{inviteErrorText(mutation.error)}</Alert> : null}

      {created ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={linkId}>Invite link</Label>
          <div className="flex gap-2">
            <Input id={linkId} readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
            <Button type="button" variant="secondary" onClick={() => void copyLink()}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="m-0 text-sm text-muted">
            Send this link to {created.email} yourself. It works once and expires{' '}
            {formatDate(created.expiresAt)}.
          </p>
        </div>
      ) : null}

      <h3 className="text-sm font-semibold m-0 mt-2">Pending invitations</h3>
      {invitations.isLoading ? (
        <div className="text-muted text-sm">Loading invitations…</div>
      ) : null}
      {invitations.isError ? <Alert>The invitations could not be loaded.</Alert> : null}
      {invitations.data ? (
        invitations.data.invitations.length === 0 ? (
          <div className="text-muted text-sm">No invitations yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Role</th>
                  <th className="py-2 pr-3 font-medium">Expires</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {invitations.data.invitations.map((i) => (
                  <tr key={i.id} className="border-b border-border last:border-0">
                    <td className="py-2 pr-3">{i.email}</td>
                    <td className="py-2 pr-3">{ROLE_LABEL[i.role]}</td>
                    <td className="py-2 pr-3">{formatDate(i.expiresAt)}</td>
                    <td className="py-2 pr-3 capitalize">{invitationStatus(i)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Card>
  );
}

/**
 * Settings → Users: member list + admin-triggered 2FA/password reset
 * (runbook MANGLER item 4) and revoking all sessions (MANGLER item 3,
 * apps/app/src/routes/session.ts). Admin-only page: an editor/viewer
 * only sees the list, no actions.
 */
export function BrugerePage() {
  const { isAdmin, user } = useAuth();
  const [resetTarget, setResetTarget] = useState<TenantUser | null>(null);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiFetch<{ users: TenantUser[] }>('/users'),
    enabled: isAdmin,
  });
  const customRoles = useCustomRoles(isAdmin);
  const roleList = customRoles.data?.roles ?? [];

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Users</h1>
        <MockBadge />
      </header>

      {!isAdmin ? (
        <Card className="p-4 flex flex-col gap-2">
          <Alert>Only administrators can view and manage users in this account.</Alert>
        </Card>
      ) : (
        <>
          <Card className="p-4 flex flex-col gap-3">
            {isLoading ? <div className="text-muted text-sm">Loading users…</div> : null}
            {isError ? <Alert>The user list could not be loaded.</Alert> : null}

            {data ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-muted border-b border-border">
                      <th className="py-2 pr-3 font-medium">Email</th>
                      <th className="py-2 pr-3 font-medium">Role</th>
                      <th className="py-2 pr-3 font-medium">Custom role</th>
                      <th className="py-2 pr-3 font-medium">2FA</th>
                      <th className="py-2 pr-3 font-medium">Created</th>
                      <th className="py-2 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.map((u) => {
                      const isSelf = u.id === user?.userId;
                      return (
                        <tr key={u.id} className="border-b border-border last:border-0">
                          <td className="py-2 pr-3">{u.email}</td>
                          <td className="py-2 pr-3">{u.role}</td>
                          <td className="py-2 pr-3">
                            <MemberRoleSelect member={u} roles={roleList} />
                          </td>
                          <td className="py-2 pr-3">{u.totpEnabled ? 'Set up' : 'Not set up'}</td>
                          <td className="py-2 pr-3">{formatDate(u.createdAt)}</td>
                          <td className="py-2 pl-3 text-right">
                            {isSelf ? (
                              <span className="text-xs text-muted">Yourself</span>
                            ) : (
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  aria-label={`Reset access: ${u.email}`}
                                  onClick={() => setResetTarget(u)}
                                >
                                  Reset access
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Card>

          <CustomRolesSection />

          <InviteSection />

          <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
            <h2 className="text-sm font-semibold m-0">Emergency response</h2>
            <p className="m-0 text-sm text-muted">
              Revokes all active sessions in this account, including your own. Use if you suspect a
              login has been compromised (runbook section 1.4).
            </p>
            <div className="flex justify-end">
              <Button variant="danger" onClick={() => setRevokeAllOpen(true)}>
                Revoke all sessions
              </Button>
            </div>
          </Card>
        </>
      )}

      <ResetMemberSheet
        open={resetTarget !== null}
        onOpenChange={(next) => {
          if (!next) setResetTarget(null);
        }}
        target={resetTarget}
      />
      <RevokeAllSessionsSheet open={revokeAllOpen} onOpenChange={setRevokeAllOpen} />
    </>
  );
}
